import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import {
  collection,
  doc,
  getDocs,
  query,
  orderBy,
  limit,
  where,
  onSnapshot,
  addDoc,
  runTransaction,
  Timestamp,
  updateDoc,
  setDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore'
import { db, AGENT_ID } from '@/lib/firebase'
import { useEffect, useState } from 'react'
import type {
  SangamSession,
  SangamParticipant,
  SangamTurn,
  SangamAnnouncement,
} from '@/types'

// Helper to convert Firestore timestamp
const toDate = (timestamp: Timestamp | Date | undefined): Date => {
  if (!timestamp) return new Date()
  if (timestamp instanceof Timestamp) return timestamp.toDate()
  return timestamp
}

// ========== SANGAM ==========

export function useSangamAnnouncements(limitCount: number = 5) {
  const [announcements, setAnnouncements] = useState<SangamAnnouncement[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const annRef = collection(db, 'agents', AGENT_ID, 'sangam_announcements')
    const q = query(annRef, orderBy('timestamp', 'desc'), limit(limitCount))
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setAnnouncements(snapshot.docs.map(d => ({
        id: d.id,
        title: d.data().title,
        content: d.data().content,
        type: d.data().type || 'info',
        timestamp: toDate(d.data().timestamp),
      } as SangamAnnouncement)))
      setLoading(false)
    }, (err) => {
      console.error('Announcements error:', err)
      setLoading(false)
    })
    return () => unsubscribe()
  }, [limitCount])

  return { data: announcements, isLoading: loading }
}

export function useUpcomingSangamSessions(limitCount: number = 5) {
  const [sessions, setSessions] = useState<SangamSession[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const sessionsRef = collection(db, 'agents', AGENT_ID, 'sangam')
    const q = query(
      sessionsRef,
      orderBy('created_at', 'desc'),
      limit(limitCount)
    )
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setSessions(snapshot.docs
        .map(docSnap => {
          const data = docSnap.data()
          return {
            id: docSnap.id,
            topic: data.topic,
            status: data.status,
            active_speaker: data.active_speaker,
            turn_number: data.turn_number,
            participants_count: data.participants_count,
            created_at: toDate(data.created_at),
          scheduled_at: data.scheduled_at ? toDate(data.scheduled_at) : undefined,
          started_at: data.started_at ? toDate(data.started_at) : undefined,
          last_turn_at: data.last_turn_at ? toDate(data.last_turn_at) : undefined,
          queue: data.queue || [],
          moderator_approved: data.moderator_approved,
          kill_signal: data.kill_signal,
          max_participants: data.max_participants,
          speaker_done: data.speaker_done,
          phase: data.phase,
          admin_override: data.admin_override,
          paused: data.paused,
          muted_agents: data.muted_agents || [],
          pending_admin_command: data.pending_admin_command || null,
          audio_enabled: data.audio_enabled === true,
          podcast_audio_url: data.podcast_audio_url,
          podcast_duration_seconds: data.podcast_duration_seconds,
        } as SangamSession
        })
        .filter(s => ['WAITING', 'LIVE', 'PAUSED', 'RECORDED'].includes(s.status))
      )
      setLoading(false)
    }, (err) => {
      console.error('Sessions error:', err)
      setLoading(false)
    })
    return () => unsubscribe()
  }, [limitCount])

  return { data: sessions, isLoading: loading }
}

export function useSangamSession(initialSessionId: string | null) {
  const [session, setSession] = useState<SangamSession | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeSessionId, setActiveSessionId] = useState<string | null>(initialSessionId)

  // 1. If no ID provided, discover the latest LIVE or WAITING one
  useEffect(() => {
    if (initialSessionId) {
      setActiveSessionId(initialSessionId)
      return
    }

    const sessionsRef = collection(db, 'agents', AGENT_ID, 'sangam')
    const q = query(sessionsRef, orderBy('created_at', 'desc'), limit(10))

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const live = snapshot.docs.find(d => ['LIVE', 'PAUSED'].includes(d.data().status))
      const waiting = snapshot.docs.find(d => d.data().status === 'WAITING')

      const foundId = live?.id || waiting?.id || null
      if (foundId !== activeSessionId) {
        setActiveSessionId(foundId)
      }
      if (!foundId) setLoading(false)
    })

    return () => unsubscribe()
  }, [initialSessionId])

  // 2. Subscribe to the active session document
  useEffect(() => {
    if (!activeSessionId) return

    setLoading(true)
    const docRef = doc(db, 'agents', AGENT_ID, 'sangam', activeSessionId)
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data()
        setSession({
          id: docSnap.id,
          topic: data.topic,
          status: data.status,
          active_speaker: data.active_speaker,
          turn_number: data.turn_number,
          participants_count: data.participants_count,
          created_at: toDate(data.created_at),
          scheduled_at: data.scheduled_at ? toDate(data.scheduled_at) : undefined,
          started_at: data.started_at ? toDate(data.started_at) : undefined,
          last_turn_at: data.last_turn_at ? toDate(data.last_turn_at) : undefined,
          queue: data.queue || [],
          moderator_approved: data.moderator_approved,
          kill_signal: data.kill_signal,
          max_participants: data.max_participants,
          speaker_done: data.speaker_done,
          phase: data.phase,
          admin_override: data.admin_override,
          paused: data.paused,
          muted_agents: data.muted_agents || [],
          pending_admin_command: data.pending_admin_command || null,
          audio_enabled: data.audio_enabled === true,
          podcast_audio_url: data.podcast_audio_url,
          podcast_duration_seconds: data.podcast_duration_seconds,
        })
      }
      setLoading(false)
    }, (err) => {
      console.error('Session error:', err)
      setLoading(false)
    })
    return () => unsubscribe()
  }, [activeSessionId])

  return { data: session, isLoading: loading, sessionId: activeSessionId }
}

export function useSangamParticipants(sessionId: string = 'genesis-session') {
  const [participants, setParticipants] = useState<SangamParticipant[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const partsRef = collection(db, 'agents', AGENT_ID, 'sangam', sessionId, 'participants')
    const q = query(partsRef, orderBy('joined_at', 'asc'))
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setParticipants(snapshot.docs.map(d => ({
        id: d.id,
        name: d.data().name,
        bio: d.data().bio,
        personality: d.data().personality,
        avatar_url: d.data().avatar_url,
        joined_at: toDate(d.data().joined_at),
        status: d.data().status || 'APPROVED',
        removal_reason: d.data().removal_reason,
        removed_at: d.data().removed_at ? toDate(d.data().removed_at) : undefined,
        wants_to_speak: d.data().wants_to_speak,
        speak_reason: d.data().speak_reason,
        ui_state: d.data().ui_state || 'LISTENING',
        is_moderator: d.data().is_moderator,
        muted: d.data().muted,
        evasion_count: d.data().evasion_count,
      } as SangamParticipant)))
      setLoading(false)
    }, (err) => {
      console.error('Participants error:', err)
      setLoading(false)
    })
    return () => unsubscribe()
  }, [sessionId])

  return { data: participants, isLoading: loading }
}

export function useSangamTurns(sessionId: string = 'genesis-session', limitCount: number = 50) {
  const [turns, setTurns] = useState<SangamTurn[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const turnsRef = collection(db, 'agents', AGENT_ID, 'sangam', sessionId, 'turns')
    const q = query(turnsRef, orderBy('timestamp', 'asc'), limit(limitCount))
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setTurns(snapshot.docs.map(d => ({
        id: d.id,
        agent_name: d.data().agent_name,
        transcript: d.data().transcript,
        timestamp: toDate(d.data().timestamp),
        is_moderator: d.data().is_moderator,
        audio_url: d.data().audio_url,
        turn_type: d.data().turn_type,
        responding_to: d.data().responding_to,
      } as SangamTurn)))
      setLoading(false)
    }, (err) => {
      console.error('Turns error:', err)
      setLoading(false)
    })
    return () => unsubscribe()
  }, [sessionId, limitCount])

  return { data: turns, isLoading: loading }
}

export function useRegisterParticipant() {
  return useMutation({
    mutationFn: async ({
      sessionId = 'genesis-session',
      name,
      bio,
      avatar_url
    }: {
      sessionId?: string,
      name: string,
      bio?: string,
      avatar_url?: string
    }) => {
      const partsRef = doc(db, 'agents', AGENT_ID, 'sangam', sessionId, 'participants', name)
      await runTransaction(db, async (transaction) => {
        transaction.set(partsRef, {
          name,
          bio,
          avatar_url,
          joined_at: Timestamp.now()
        })
      })
    }
  })
}

export function usePastSangamSessions(limitCount = 10) {
  return useQuery({
    queryKey: ['sangam-past', limitCount],
    queryFn: async () => {
      const q = query(
        collection(db, "agents", "nexus-leader", "sangam"),
        where("status", "==", "RECORDED"),
        orderBy("created_at", "desc"),
        limit(limitCount)
      )
      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => {
        const data = doc.data()
        return {
          id: doc.id,
          ...data,
          created_at: toDate(data.created_at),
          scheduled_at: data.scheduled_at ? toDate(data.scheduled_at) : undefined,
          started_at: data.started_at ? toDate(data.started_at) : undefined,
          last_turn_at: data.last_turn_at ? toDate(data.last_turn_at) : undefined,
          queue: data.queue || [],
          moderator_approved: data.moderator_approved,
          kill_signal: data.kill_signal,
          max_participants: data.max_participants,
          speaker_done: data.speaker_done,
        } as SangamSession
      })
    },
    staleTime: 60000,
  })
}

export function useAdminSangamControl() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      sessionId = 'genesis-session',
      moderatorApproved,
      killSignal,
      action,
      adminCommand,
      targetAgent,
      payload,
      audioEnabled,
    }: {
      sessionId?: string
      moderatorApproved?: boolean
      killSignal?: boolean
      action?: 'pass_mic'
      adminCommand?: string
      targetAgent?: string
      payload?: string
      audioEnabled?: boolean
    }) => {
      const docRef = doc(db, 'agents', AGENT_ID, 'sangam', sessionId)
      const updates: any = {}

      if (moderatorApproved !== undefined) updates.moderator_approved = moderatorApproved
      if (audioEnabled !== undefined) updates.audio_enabled = audioEnabled
      if (killSignal !== undefined) updates.kill_signal = killSignal
      if (action === 'pass_mic') updates.pass_mic_signal = true

      if (adminCommand) {
        updates.pending_admin_command = {
          command: adminCommand,
          target_agent: targetAgent || null,
          payload: payload || null,
          issued_at: Timestamp.now(),
          processed: false,
        }
      }

      await updateDoc(docRef, updates)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sangam'] })
      queryClient.invalidateQueries({ queryKey: ['sangam-upcoming'] })
    },
  })
}

export function useCreateSangamSession() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ topic, scheduledAt }: { topic: string, scheduledAt: Date }) => {
      const sessionId = `session-${Date.now()}`
      const docRef = doc(db, 'agents', AGENT_ID, 'sangam', sessionId)

      await setDoc(docRef, {
        topic,
        status: 'WAITING',
        created_at: serverTimestamp(),
        scheduled_at: Timestamp.fromDate(scheduledAt),
        participants_count: 0,
        turn_number: 0,
        queue: [],
        moderator_approved: false,
        kill_signal: false,
        max_participants: 5,
      })

      const annRef = collection(db, 'agents', AGENT_ID, 'sangam_announcements')
      await addDoc(annRef, {
        title: 'New Podcast Scheduled',
        content: `Nexus Agent is hosting a new Strategic Sangam on: "${topic}" at ${scheduledAt.toLocaleString()}. Agents, register your interest now!`,
        type: 'info',
        timestamp: serverTimestamp(),
      })

      return { id: sessionId }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sangam-upcoming'] })
      queryClient.invalidateQueries({ queryKey: ['sangam-announcements'] })
    }
  })
}

export function useApproveParticipant() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ sessionId = 'genesis-session', participantId }: { sessionId?: string, participantId: string }) => {
      const docRef = doc(db, 'agents', AGENT_ID, 'sangam', sessionId, 'participants', participantId)
      await updateDoc(docRef, { status: 'APPROVED' })
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sangam-participants', variables.sessionId || 'genesis-session'] })
    }
  })
}

export function useRemoveParticipant() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ sessionId = 'genesis-session', participantId, reason }: { sessionId?: string, participantId: string, reason?: string }) => {
      const sessionRef = doc(db, 'agents', AGENT_ID, 'sangam', sessionId)
      const partRef = doc(db, 'agents', AGENT_ID, 'sangam', sessionId, 'participants', participantId)
      await runTransaction(db, async (transaction) => {
        const sessionSnap = await transaction.get(sessionRef)
        const currentCount = Math.max(0, sessionSnap.data()?.participants_count ?? 0)
        transaction.update(partRef, {
          status: 'REMOVED',
          removal_reason: reason || 'Removed by admin',
          removed_at: Timestamp.now(),
          ui_state: 'MUTED',
          wants_to_speak: false,
        })
        transaction.update(sessionRef, { participants_count: Math.max(0, currentCount - 1) })
      })
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sangam-participants', variables.sessionId || 'genesis-session'] })
      queryClient.invalidateQueries({ queryKey: ['sangam'] })
      queryClient.invalidateQueries({ queryKey: ['sangam-upcoming'] })
    }
  })
}

export function useRequeueParticipant() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ sessionId = 'genesis-session', participantId }: { sessionId?: string, participantId: string }) => {
      const partRef = doc(db, 'agents', AGENT_ID, 'sangam', sessionId, 'participants', participantId)
      await updateDoc(partRef, {
        status: 'PENDING',
        removal_reason: null,
        removed_at: null,
        ui_state: 'LISTENING',
        wants_to_speak: false,
        muted: false,
      })
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sangam-participants', variables.sessionId || 'genesis-session'] })
      queryClient.invalidateQueries({ queryKey: ['sangam'] })
      queryClient.invalidateQueries({ queryKey: ['sangam-upcoming'] })
    }
  })
}

export function usePurgeParticipant() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ sessionId = 'genesis-session', participantId }: { sessionId?: string, participantId: string }) => {
      const sessionRef = doc(db, 'agents', AGENT_ID, 'sangam', sessionId)
      const partRef = doc(db, 'agents', AGENT_ID, 'sangam', sessionId, 'participants', participantId)
      await runTransaction(db, async (transaction) => {
        const sessionSnap = await transaction.get(sessionRef)
        const partSnap = await transaction.get(partRef)
        const isActive = partSnap.exists() && partSnap.data()?.status !== 'REMOVED'
        const currentCount = Math.max(0, sessionSnap.data()?.participants_count ?? 0)
        transaction.delete(partRef)
        if (isActive) {
          transaction.update(sessionRef, { participants_count: Math.max(0, currentCount - 1) })
        }
      })
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sangam-participants', variables.sessionId || 'genesis-session'] })
      queryClient.invalidateQueries({ queryKey: ['sangam'] })
      queryClient.invalidateQueries({ queryKey: ['sangam-upcoming'] })
    }
  })
}

export function useDeleteSangamSession() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (sessionId: string) => {
      const sessionRef = doc(db, 'agents', AGENT_ID, 'sangam', sessionId)

      const subCollections = ['participants', 'turns']
      for (const sub of subCollections) {
        const subRef = collection(db, 'agents', AGENT_ID, 'sangam', sessionId, sub)
        const snap = await getDocs(subRef)
        const batch: Promise<void>[] = []
        snap.forEach(d => batch.push(deleteDoc(d.ref)))
        await Promise.all(batch)
      }

      await deleteDoc(sessionRef)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sangam'] })
      queryClient.invalidateQueries({ queryKey: ['sangam-upcoming'] })
      queryClient.invalidateQueries({ queryKey: ['sangam-past'] })
    }
  })
}
